import { useState } from "react";
import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import { exportBackup } from "../api/functions";

export function AdminLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    "px-3 py-1.5 rounded-md text-sm font-medium transition " +
    (isActive
      ? "bg-indigo-50 text-indigo-700"
      : "text-slate-600 hover:text-slate-900");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/admin" className="font-semibold text-slate-800">
            Dinflow Dashboard · <span className="text-indigo-600">Admin</span>
          </Link>
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <BackupButton />
            <span>{user?.email}</span>
            <button onClick={handleSignOut} className="text-indigo-600 hover:underline">
              Sign out
            </button>
          </div>
        </div>
        <nav className="max-w-6xl mx-auto px-6 pb-2 flex gap-1">
          <NavLink to="/admin" end className={navLinkClass}>Accounts</NavLink>
          <NavLink to="/admin/licenses" className={navLinkClass}>All licenses</NavLink>
          <NavLink to="/admin/admins" className={navLinkClass}>Admins</NavLink>
        </nav>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Downloads a full, ENCRYPTED disaster-recovery backup: signing key + licenses +
 * accounts + portal auth users, AES-256-GCM under a passphrase the admin enters.
 * The signing key never leaves the server in plaintext. Restore is
 * functions/scripts/import-backup.ts. Keep the .enc file AND its passphrase safe,
 * in separate places — lose the passphrase and the backup can't be opened.
 */
function BackupButton() {
  const [busy, setBusy] = useState(false);

  const onBackup = async () => {
    const passphrase = window.prompt(
      "Enter a passphrase to ENCRYPT this backup.\n\n" +
      "You'll need this exact passphrase to restore. Save it in your password " +
      "manager — if you lose it, the backup cannot be opened.\n\n" +
      "Tip: use the SAME passphrase every time so all your dated backups open with it.",
    );
    if (passphrase === null) return;                 // cancelled
    if (passphrase.length < 8) {
      alert("Passphrase must be at least 8 characters.");
      return;
    }
    const confirmPass = window.prompt("Re-enter the passphrase to confirm:");
    if (confirmPass === null) return;
    if (confirmPass !== passphrase) {
      alert("Passphrases did not match. Backup cancelled.");
      return;
    }

    setBusy(true);
    try {
      const res  = await exportBackup({ passphrase });
      const blob = new Blob([res.encrypted], { type: "application/octet-stream" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10); // yyyy-MM-dd
      a.href = url;
      a.download = `dinflow-prod-backup-${stamp}.enc`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      alert(
        "Encrypted backup downloaded.\n\n" +
        `• ${res.meta.licenseCount} licenses\n` +
        `• ${res.meta.accountCount} accounts\n` +
        `• ${res.meta.authUserCount} portal users\n` +
        `• Signing key: ${res.meta.hasSigningKey ? "INCLUDED ✓" : "MISSING ✗"}\n\n` +
        "Keep this file AND its passphrase safe, in separate places.",
      );
    } catch (err) {
      alert((err as { message?: string }).message ?? "Backup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={onBackup}
      disabled={busy}
      title="Download an encrypted backup: signing key + licenses + accounts + portal users"
      className="text-indigo-600 hover:underline disabled:opacity-50"
    >
      {busy ? "Backing up…" : "⬇ Backup"}
    </button>
  );
}
